import * as React from "react";
import { css } from "@emotion/core";
import { Button, ButtonGroup, Spinner } from "@blueprintjs/core";
import Map, { Marker, Source, Layer, useControl } from "react-map-gl/mapbox";
import { PointCloudLayer } from "@deck.gl/layers";
import { COORDINATE_SYSTEM } from "@deck.gl/core";
import { MapboxOverlay } from "@deck.gl/mapbox";
import { useSelector } from "@xstate/store/react";
import { virtualizerStore } from "../../state/virtualizerStore";
import { ImagesMachineContext } from "components/Orders/state/imagesMachine";
import { useUploadMachineSelector } from "../../context/upload-machine";
import { generateUniqueFileIdentifier } from "../../state/uploadMachine";
import { FileUploadActor } from "../../state/uploadMachine/types";
import { LidarMachineContext } from "components/Orders/state/lidarMachine";
import { GeometryMachineContext } from "components/Orders/state/geometryMachine";
import { ProcessedPointCloud } from "components/Orders/state/types";

function MarkerCircle({
  index,
  id,
  longitude,
  latitude,
}: {
  index: number;
  id: string;
  longitude: number;
  latitude: number;
}) {
  const imagesMachine = ImagesMachineContext.useActorRef();

  const missing = ImagesMachineContext.useSelector((state) => {
    const upload = state.context.uploads.find((upload) => upload.id === id);

    return (
      upload &&
      upload.file instanceof File === false &&
      upload.status !== "uploaded"
    );
  });

  const addedNotUploaded = ImagesMachineContext.useSelector((state) => {
    const upload = state.context.uploads.find((upload) => upload.id === id);

    return (
      upload &&
      upload.file instanceof File === true &&
      upload.status !== "uploaded"
    );
  });

  const hover = ImagesMachineContext.useSelector(
    (state) => state.context.hover[id]
  );
  const selected = ImagesMachineContext.useSelector((state) =>
    state.context.selected.includes(id)
  );

  const virtualizer = useSelector(
    virtualizerStore,
    (state) => state.context.virtualizer
  );

  const upload = ImagesMachineContext.useSelector((state) =>
    state.context.uploads.find((upload) => upload.id === id)
  );

  const completed = useUploadMachineSelector((state) => {
    if (!upload || !(upload.file instanceof File)) return false;
    const fileIdentifier = generateUniqueFileIdentifier(upload.file);
    return state.context.uploadedFiles.includes(fileIdentifier.name);
  });

  const active = useUploadMachineSelector((state) => {
    if (!upload || !(upload.file instanceof File)) return false;
    const fileIdentifier = generateUniqueFileIdentifier(upload.file);
    return state.context.fileUploadActors.some((actor: FileUploadActor) => {
      return actor.id === `file-${fileIdentifier.name}`;
    });
  });

  return (
    <Marker
      key={id}
      longitude={longitude}
      latitude={latitude}
      css={css`
        z-index: ${selected || hover ? 1 : 0};
      `}
      onClick={() => {
        if (selected) {
          imagesMachine.send({ type: "MARKER_DESELECT", id });
        } else {
          imagesMachine.send({ type: "MARKER_SELECT", id });

          virtualizer?.scrollToIndex(index, {
            align: "center",
            behavior: "auto",
          });
        }
      }}
    >
      <svg
        width="20"
        height="20"
        onMouseOver={() => imagesMachine.send({ type: "MARKER_OVER", id })}
        onMouseOut={() => imagesMachine.send({ type: "MARKER_OUT", id })}
        style={{
          transform: selected || hover ? "scale(1.25)" : "scale(1)",
          transition: "transform 200ms ease-in",
        }}
      >
        <circle
          cx="10"
          cy="10"
          r="8"
          fill={
            selected
              ? "var(--fybr-blue)"
              : completed || upload?.status === "uploaded"
              ? "var(--success)"
              : active
              ? "var(--fybr-blue)"
              : missing
              ? "var(--danger)"
              : addedNotUploaded
              ? "var(--neutral-5)"
              : "var(--success)"
          }
          fillOpacity={selected || hover || active ? 1 : 0.65}
          stroke={"black"}
          strokeWidth={hover ? 2 : 1}
          style={{ cursor: "pointer" }}
        />
      </svg>
    </Marker>
  );
}

function DeckOverlay({ layers }: { layers: PointCloudLayer[] }): null {
  useControl(() => new MapboxOverlay({ interleaved: true, layers }));

  return null;
}

interface PointCloudPreviewProps {
  pointclouds: ProcessedPointCloud[];
}

function PointCloudPreview(props: PointCloudPreviewProps) {
  const layers = props.pointclouds
    .filter((pointcloud) => pointcloud.data?.positions && pointcloud.data?.bbox)
    .map((pointcloud) => {
      return new PointCloudLayer({
        id: `pointcloud-${pointcloud.id}`,
        data: {
          length: pointcloud.data.positions.length / 3,
          attributes: {
            getPosition: { value: pointcloud.data.positions, size: 3 },
            getColor: pointcloud.data.colors
              ? { value: pointcloud.data.colors, size: 4 }
              : undefined,
          },
        },
        pointSize: 3,
        coordinateSystem: COORDINATE_SYSTEM.METER_OFFSETS,
        coordinateOrigin: pointcloud.data.origin,
      });
    });

  if (layers.length === 0) return null;

  return <DeckOverlay layers={layers} />;
}

interface MapViewProps {
  mapRef: React.RefObject<mapboxgl.Map>;
  onZoomEnd: (event: any) => void;
  mapCenter: [number, number];
  zoom?: number;
}

export default function MapView(props: MapViewProps) {
  const [mapStyle, setMapStyle] = React.useState(
    "mapbox://styles/mapbox/outdoors-v12"
  );

  const imageUploads = ImagesMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const lidarUploads = LidarMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const geometryUploads = GeometryMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const imagesProcessing = ImagesMachineContext.useSelector((state) =>
    state.matches("processing")
  );

  const lidarProcessing = LidarMachineContext.useSelector((state) =>
    state.matches("processing")
  );

  const geometryProcessing = GeometryMachineContext.useSelector((state) =>
    state.matches("processing")
  );

  const geometry = React.useMemo(() => {
    return geometryUploads.filter((upload) => upload.data?.collection);
  }, [geometryUploads]);

  const loading = imagesProcessing || lidarProcessing || geometryProcessing;

  return (
    <div
      css={css`
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        overflow-y: auto;
        background-color: var(--neutral-7);
        border: 1px solid var(--neutral-5);
        position: relative;
      `}
    >
      <div
        css={css`
          position: absolute;
          display: flex;
          width: 100%;
          height: 100%;
          left: 0;
          top: 0;
          justify-content: center;
          align-items: center;
          z-index: 1;
          background-color: rgba(0, 0, 0, 0.4);
          opacity: ${loading ? 1 : 0};
          transition: opacity 200ms ease-in-out ${loading ? "300ms" : "0ms"};
          pointer-events: ${loading ? "auto" : "none"};
        `}
      >
        <Spinner />
      </div>
      <div
        css={css`
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 10;
        `}
      >
        <ButtonGroup>
          <Button
            size="small"
            intent={
              mapStyle === "mapbox://styles/mapbox/outdoors-v12"
                ? "primary"
                : "none"
            }
            onClick={() => setMapStyle("mapbox://styles/mapbox/outdoors-v12")}
          >
            Outdoors
          </Button>
          <Button
            size="small"
            intent={
              mapStyle === "mapbox://styles/mapbox/satellite-v9"
                ? "primary"
                : "none"
            }
            onClick={() => setMapStyle("mapbox://styles/mapbox/satellite-v9")}
          >
            Satellite
          </Button>
        </ButtonGroup>
      </div>
      <Map
        ref={props.mapRef}
        initialViewState={{
          longitude: props.mapCenter[0],
          latitude: props.mapCenter[1],
          zoom: props.zoom ?? 14,
        }}
        onZoomEnd={props.onZoomEnd}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_KEY}
        style={{ width: "100%", height: "100%" }}
        mapStyle={mapStyle}
      >
        {geometry.map((geometry) => (
          <Source
            key={geometry.id}
            type="geojson"
            data={geometry.data?.collection}
          >
            <Layer
              type="line"
              paint={{
                "line-color": `rgba(255, 255, 255, 1)`,
                "line-width": 2,
              }}
            />
          </Source>
        ))}
        {imageUploads.map((image, index) =>
          image.data?.exif ? (
            <MarkerCircle
              index={index}
              key={image.id}
              id={image.id}
              longitude={image.data.exif.geoLocation.longitude}
              latitude={image.data.exif.geoLocation.latitude}
            />
          ) : null
        )}
        <PointCloudPreview pointclouds={lidarUploads} />
      </Map>
    </div>
  );
}
